/**
 * Ops simulation T16 — shared on-device manager approval ("Approval required").
 *
 * A protected action (a return above the approval limit today; credit over
 * limit, the tax switch and cash differences next) opens this dialog. A
 * manager or owner picks themselves, types their POS PIN, and the dialog hands
 * the caller a single-use approval token to retry the action with. When no
 * manager is in the store, `onAskRemotely` sends the request to one instead.
 */

import { useEffect, useState } from 'react';
import { Alert, Button, Modal } from '@bbpos/ui';
import { ApiError } from '@/lib/api-client';
import {
  managerApprovalService,
  type Approver,
  type ManagerApprovalAction,
  type ManagerApprovalGrant,
} from '@/services/manager-approval.service';

export interface ManagerApprovalDialogProps {
  open: boolean;
  action: ManagerApprovalAction;
  /** Why approval is needed, shown to the manager (e.g. the server's message with the amount). */
  reason: string;
  /** The record being approved (a bill for a return). */
  subjectId?: string;
  /** The most the approval covers, in minor units, when the caller knows it. */
  amount?: number;
  locationId?: string;
  onApproved: (grant: ManagerApprovalGrant) => void;
  onCancel: () => void;
  /** Fallback when no manager is here: send the request to a manager. */
  onAskRemotely?: () => void;
}

const MAX_PIN = 6;
const KEYS = ['1', '2', '3', '4', '5', '6', '7', '8', '9'];

export function ManagerApprovalDialog({
  open,
  action,
  reason,
  subjectId,
  amount,
  locationId,
  onApproved,
  onCancel,
  onAskRemotely,
}: ManagerApprovalDialogProps) {
  const [approvers, setApprovers] = useState<Approver[] | null>(null);
  const [approverId, setApproverId] = useState<string | null>(null);
  const [pin, setPin] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setPin('');
    setError(null);
    setApprovers(null);
    let cancelled = false;
    managerApprovalService
      .listApprovers(locationId)
      .then((list) => {
        if (cancelled) return;
        setApprovers(list);
        setApproverId(list.length === 1 ? list[0].id : null);
      })
      .catch(() => {
        if (!cancelled) {
          setApprovers([]);
          setError('Could not load the managers for this store.');
        }
      });
    return () => {
      cancelled = true;
    };
  }, [open, locationId]);

  const press = (digit: string) => {
    setError(null);
    setPin((p) => (p.length < MAX_PIN ? p + digit : p));
  };

  const submit = async () => {
    if (!approverId || pin.length < 4) return;
    setSubmitting(true);
    setError(null);
    try {
      const grant = await managerApprovalService.verify({
        approver_id: approverId,
        pin,
        action,
        ...(subjectId ? { subject_id: subjectId } : {}),
        ...(amount !== undefined ? { amount } : {}),
        ...(locationId ? { location_id: locationId } : {}),
      });
      onApproved(grant);
    } catch (err) {
      setPin('');
      setError(err instanceof ApiError ? err.message : 'Could not check the PIN. Try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onCancel}
      title="Manager approval"
      footer={
        <>
          <Button variant="secondary" onClick={onCancel} disabled={submitting}>
            Cancel
          </Button>
          {onAskRemotely && (
            <Button variant="secondary" onClick={onAskRemotely} disabled={submitting}>
              Send to a manager
            </Button>
          )}
          <Button
            variant="primary"
            loading={submitting}
            disabled={!approverId || pin.length < 4}
            onClick={submit}
          >
            Approve
          </Button>
        </>
      }
    >
      <div className="space-y-4 animate-fade-in">
        <div className="flex items-start gap-3 rounded-xl bg-gradient-to-r from-warning-50 to-danger-50 dark:from-warning-900/30 dark:to-danger-900/30 border border-warning-200 dark:border-warning-800 p-4">
          <div className="w-10 h-10 shrink-0 rounded-lg bg-gradient-to-br from-warning-500 to-danger-500 flex items-center justify-center text-white">
            <svg
              className="h-5 w-5"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
              <polyline points="9 12 11 14 15 10" />
            </svg>
          </div>
          <p className="text-sm text-neutral-800 dark:text-neutral-100">{reason}</p>
        </div>

        <div>
          <p className="text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
            Who is approving?
          </p>
          {approvers === null ? (
            <p className="text-sm text-neutral-500 dark:text-neutral-400">Loading managers…</p>
          ) : approvers.length === 0 ? (
            <p className="text-sm text-neutral-500 dark:text-neutral-400">
              No manager at this store has a POS PIN yet.
            </p>
          ) : (
            <div className="flex flex-wrap gap-2" role="radiogroup" aria-label="Approver">
              {approvers.map((a) => (
                <button
                  key={a.id}
                  type="button"
                  role="radio"
                  aria-checked={approverId === a.id}
                  onClick={() => {
                    setApproverId(a.id);
                    setError(null);
                  }}
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm transition-all duration-200 ${
                    approverId === a.id
                      ? 'border-brand-500 bg-brand-50 text-brand-700 dark:bg-brand-900/40 dark:text-brand-200 shadow-md'
                      : 'border-neutral-200 dark:border-neutral-700 text-neutral-700 dark:text-neutral-200 hover:bg-brand-50 dark:hover:bg-neutral-800'
                  }`}
                >
                  <span>{a.name}</span>
                  <span className="text-xs rounded-full px-2 py-0.5 bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-200">
                    {a.role === 'OWNER' ? 'Owner' : 'Manager'}
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>

        <div>
          <p className="text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">PIN</p>
          <div
            className="flex justify-center gap-2 mb-3"
            aria-label={`PIN, ${pin.length} digits entered`}
            data-testid="pin-dots"
          >
            {Array.from({ length: MAX_PIN }, (_, i) => (
              <span
                key={i}
                className={`w-3 h-3 rounded-full transition-all duration-200 ${
                  i < pin.length ? 'bg-brand-600 scale-110' : 'bg-neutral-200 dark:bg-neutral-700'
                }`}
              />
            ))}
          </div>
          <div className="grid grid-cols-3 gap-2 max-w-xs mx-auto">
            {KEYS.map((k) => (
              <KeyButton key={k} label={k} onClick={() => press(k)} disabled={submitting} />
            ))}
            <KeyButton label="Clear" onClick={() => setPin('')} disabled={submitting} />
            <KeyButton label="0" onClick={() => press('0')} disabled={submitting} />
            <KeyButton
              label="Back"
              icon={<span aria-hidden="true">⌫</span>}
              onClick={() => setPin((p) => p.slice(0, -1))}
              disabled={submitting}
            />
          </div>
        </div>

        {error && <Alert variant="danger" description={error} />}
      </div>
    </Modal>
  );
}

function KeyButton({
  label,
  icon,
  onClick,
  disabled,
}: {
  label: string;
  icon?: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      onClick={onClick}
      disabled={disabled}
      className="h-12 rounded-lg bg-neutral-100 dark:bg-neutral-800 text-lg font-semibold text-neutral-900 dark:text-white hover:bg-brand-50 dark:hover:bg-neutral-700 active:scale-95 transition-all duration-200 disabled:opacity-50 flex items-center justify-center"
    >
      {icon ?? (label.length === 1 ? label : <span className="text-sm">{label}</span>)}
    </button>
  );
}
